/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { SocialShares, Modal } from "components/common";
import { colors } from "theme";
import { isValidEmail } from "@tomocredit/common-fe/utils";

export const SEND_BOOST_REFERRAL = gql`
  mutation sendBoostReferral(
    $email: String!
  ) {
    sendBoostReferral(email: $email) {
      status
      code
      message
    }
  }
`;

export const BoostReferralModal = ({ isOpen, closeModal }) => {
  return (
    <Modal
      isOpen={isOpen}
      close={closeModal}
      options={{ showCloseButton: false }}
    >
      <div className="flex flex-col justify-center items-center bg-white rounded-xl px-4 py-10" style={{ maxWidth: 350 }}>
        <div className="flex flex-col justify-center items-center space-y-2 font-medium">
          <div className="underline text-lg font-semibold">Here is how it works</div>
          <div className="text-lg font-semibold">Your friend joins:</div>
          <div>VIP = <span className="font-bold text-xl">$100 off</span> <span className="text-xs">[$20 if monthly]</span></div>
          <div>Premium = <span className="font-bold text-xl">$50 off</span> <span className="text-xs">[$10 if monthly]</span></div>
          <div>Basic = <span className="font-bold text-xl">$20 off</span> <span className="text-xs">[$5 if monthly]</span></div>
        </div>
        <div className="mt-10 mx-4 text-center text-sm">Promo only applies to successful referrals through your individual referral code.</div>
        <div className="mt-4 text-center text-xs font-semibold">Credits are automatically applied to your next payment.</div>
        <button type="button" className="mt-8 rounded-full px-20 py-2 bg-boost-light-purple text-white font-semibold" onClick={closeModal}>ok</button>
      </div>
    </Modal>
  );
};

export const BoostReferral = ({ referralCode, currentTier, currentTerm, email }) => {
  const [copyLinkText, setCopyLinkText] = useState("copy referral link");
  const [referralEmail, setReferralEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteButtonText, setInviteButtonText] = useState("invite");
  const [inviteErrorMessage, setInviteErrorMessage] = useState("");
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [sendBoostReferral] = useMutation(SEND_BOOST_REFERRAL);

  const BOOST_REFERRAL = `BOOST_${referralCode}`;
  const REFERRAL_LINK = `https://tomoboost.com/boost?referralCode=${BOOST_REFERRAL}&utm_source=webreferral`;
  const SHARE_TITLE = `Use my referral link or code ${BOOST_REFERRAL} and get up to $100 cash when you open an account with TomoBoost. Start building and repairing your credit today! #tomoboost\n\n`;

  const handleInviteButtonClicked = () => {
    if (loading) {
      return;
    }
    if (!isValidEmail(referralEmail)) {
      setInviteErrorMessage("Email address is required");
      return;
    }

    setLoading(true);
    setInviteErrorMessage("");
    setInviteButtonText("sending");
    if (typeof window?.gtag === "function") {
      window.gtag("event", "boost_referral_invited", {
        source: "web",
        referrerTier: currentTier,
        referrerTerm: currentTerm,
        referrerEmail: email,
        invitedEmail: referralEmail,
      });
    }
    sendBoostReferral({
      variables: {
        email: referralEmail,
      },
    }).then(() => {
      setInviteButtonText("sent!");

      setTimeout(() => {
        setInviteButtonText("invite");
      }, 2000);
    }).catch((error) => {
      setInviteButtonText("invite");
      const message = error.graphQLErrors[0]?.message;
      setInviteErrorMessage(message);
    }).finally(() => {
      setLoading(false);
    });
  };

  return (
    <div className="mt-10 w-full p-5 flex flex-col border-2 border-white rounded-xl border-glow-white-sharp bg-black bg-opacity-50">
      {showReferralModal && <BoostReferralModal isOpen={showReferralModal} closeModal={() => setShowReferralModal(false)} />}
      <div className="text-2xl font-semibold text-boost-lighter-purple text-left">
        Invite Friends, Get $100 Cash Each<span className="text-white">.</span>
      </div>
      <div className="text-left text-sm">Earn up to $100<span className="align-top text-xxxs">ⓘ</span> unlimited times with successful referrals.</div>
      <div className="mt-4 w-full flex flex-col">
        <div>Share your link</div>
        <input
          disabled
          type="text"
          value={REFERRAL_LINK}
          className="mt-2 text-black rounded p-2 bg-white text-sm w-full"
        />
        <button
          className="mt-1 bg-boost-primary text-white rounded rounded-2 p-2 w-full"
          type="button"
          style={{
            outline: "none",
          }}
          onClick={() => {
            navigator.clipboard.writeText(REFERRAL_LINK);
            if (typeof window?.gtag === "function") {
              window.gtag("event", "boost_referral_copied", {
                source: "web",
                tier: currentTier,
                term: currentTerm,
                email,
              });
            }
            setCopyLinkText("Copied!");
            setTimeout(() => {
              setCopyLinkText("Copy referral link");
            }, 5000);
          }}
        >
          {copyLinkText}
        </button>
      </div>
      <div className="mt-5 flex flex-col">
        <div>Invite friends & family</div>
        <div className="flex flex-row mt-2">
          <input
            placeholder="Enter your friend's email"
            className="w-2/3 text-black rounded p-2"
            value={referralEmail}
            onChange={(e) => {
              setInviteErrorMessage("");
              setReferralEmail(e.target.value);
            }}
          />
          <button
            type="button"
            className="w-1/3 text-white p-2 rounded rounded-2 ml-2 bg-boost-primary-purple hover:bg-boost-light-purple focus:outline-none"
            onClick={handleInviteButtonClicked}
          >
            {inviteButtonText}
          </button>
        </div>
        { inviteErrorMessage && <span className="text-left mt-2">{inviteErrorMessage}</span> }
      </div>
      <div className="mt-5 flex flex-col">
        <div className="mb-2">Share your link</div>
        <SocialShares
          size={24}
          iconBgColor={colors["gray-primary"]}
          logoColor="white"
          url={REFERRAL_LINK}
          title={SHARE_TITLE}
          hashtag="#TomoBoost"
        />
      </div>
      <div className="mt-5 self-start">
        <span>ⓘ</span>
        <button
          className="ml-2 underline focus:outline-none"
          type="button"
          onClick={() => setShowReferralModal(true)}>
            Click here</button>
        <span> for referral details</span>
      </div>
    </div>
  );
};
